import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EventBusService } from '@shared';
import { filter, tap } from 'rxjs/operators';
import { OrderSide } from '@shared-types';
import { OrderFormInputData } from '../../models/order-form.model';
import { ORDER_FORM_EVENTS } from './order-form-events.model';

/**
 * Bridges `order-form.open` to the order-form route.
 *
 * Instantiated at bootstrap (see `provideEventDrivenStartup`) so producers can request the
 * form from any route without a mounted consumer. `isin`/`side` go in the URL because the
 * route requires them; the rest of the payload (`isEdit`, `orderId`, `formType`, `validRanges`, …)
 * rides along as `NavigationExtras.state` for `OrderFormComponent` to merge.
 *
 * `state` rather than `info` because the browser persists it in `history.state`: a reload or a
 * back/forward still restores the edit context, where `info` would be gone and an edit would
 * silently downgrade into a new order.
 */
@Service()
export class OrderFormNavigatorService {
	private readonly router = inject(Router);
	private readonly eventBus = inject(EventBusService);

	constructor() {
		this.eventBus
			.on$<OrderFormInputData>(ORDER_FORM_EVENTS.OPEN)
			.pipe(
				filter(Boolean),
				tap(wrapped => this.eventBus.clearSnapshot(ORDER_FORM_EVENTS.OPEN, wrapped.id)),
				takeUntilDestroyed()
			)
			.subscribe(wrapped => {
				this.openOrderForm(wrapped.data);
			});
	}

	private openOrderForm(data: OrderFormInputData | undefined): void {
		// The route needs an isin; without one there is nothing to open.
		if (!data?.isin) {
			return;
		}

		this.router.navigate(['/order-form', data.isin, data.side ?? OrderSide.Buy], { state: data });
	}
}
